import { Plus, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createRoutine, deleteRoutine, updateRoutine } from "wasp/client/operations";
import useTranslation from "../../../client/hooks/useTranslation";
import { Button } from "../../../client/components/ui/button";
import { Input } from "../../../client/components/ui/input";
import type { LiteRoutine } from "../../data/useLiteDiscipline";
import { useLiteActions } from "../../shell/LiteActions";
import { LiteDrawer } from "../../ui/LiteDrawer";
import { labelCls } from "../../ui/ui";

const isPlanLimit = (e: unknown) => {
  const err = e as { statusCode?: number; status?: number } | null;
  return err?.statusCode === 403 || err?.status === 403;
};

/** Création / édition d'une routine : nom + items. Fréquence daily à la création, inchangée à l'édition. */
export function RoutineDrawer({ open, routine, onClose }: {
  open: boolean;
  routine: LiteRoutine | null;
  onClose: () => void;
}) {
  const t = useTranslation().lite.discipline;
  const { openUpgrade } = useLiteActions();
  const [name, setName] = useState("");
  const [items, setItems] = useState<string[]>([""]);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const itemRefs = useRef<(HTMLInputElement | null)[]>([]);
  const focusNext = useRef(false);

  useEffect(() => {
    if (!open) return;
    setName(routine?.name ?? "");
    setItems(routine?.checklistItems.length ? [...routine.checklistItems] : [""]);
    setError(null);
    setConfirmDelete(false);
  }, [open, routine]);

  // Focus le dernier item seulement quand il vient d'être ajouté via addItem (pas à l'ouverture du tiroir).
  useEffect(() => {
    if (focusNext.current) {
      itemRefs.current[items.length - 1]?.focus();
      focusNext.current = false;
    }
  }, [items.length]);

  // Items nettoyés et dédoublonnés : les complétions sont stockées par libellé.
  const cleanItems = Array.from(new Set(items.map(i => i.trim()).filter(Boolean)));
  const valid = name.trim().length > 0 && cleanItems.length > 0;

  const fail = (e: unknown) => {
    if (isPlanLimit(e)) { onClose(); openUpgrade(); return; }
    console.error(e);
    setError(t.form.error);
  };

  const save = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      if (routine) await updateRoutine({ id: routine.id, name: name.trim(), checklistItems: cleanItems });
      else await createRoutine({ name: name.trim(), checklistItems: cleanItems, frequency: "daily", isActive: true });
      onClose();
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!routine || busy) return;
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setBusy(true);
    try { await deleteRoutine({ id: routine.id }); onClose(); } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const setItem = (i: number, v: string) => setItems(prev => prev.map((x, k) => (k === i ? v : x)));
  const addItem = () => { focusNext.current = true; setItems(prev => [...prev, ""]); };
  const removeItem = (i: number) => setItems(prev => (prev.length > 1 ? prev.filter((_, k) => k !== i) : [""]));

  return (
    <LiteDrawer
      open={open}
      title={routine ? t.routine.edit : t.routine.add}
      onClose={onClose}
      footer={
        <>
          {routine ? (
            <Button variant="ghost" className="text-pnlNegative" onClick={remove} disabled={busy}>
              <Trash2 className="h-4 w-4 mr-1.5" />{confirmDelete ? t.form.confirmDelete : t.form.delete}
            </Button>
          ) : <span />}
          <Button onClick={save} disabled={!valid || busy}>{t.form.save}</Button>
        </>
      }
    >
      <div>
        <label className={labelCls} htmlFor="lite-routine-name">{t.form.routineName}</label>
        <Input id="lite-routine-name" value={name} placeholder={t.form.routineNamePlaceholder} onChange={e => setName(e.target.value)} autoFocus />
      </div>
      <div>
        <div className={labelCls}>{t.form.items}</div>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                ref={el => { itemRefs.current[i] = el; }}
                value={it}
                placeholder={t.form.itemPlaceholder}
                aria-label={t.form.itemPlaceholder}
                onChange={e => setItem(i, e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); if (i === items.length - 1) addItem(); else itemRefs.current[i + 1]?.focus(); } }}
              />
              <button type="button" onClick={() => removeItem(i)} className="w-9 h-9 shrink-0 grid place-items-center rounded-lg hover:bg-muted text-muted-foreground" aria-label={t.form.removeItem}><X size={16} /></button>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" className="mt-3" onClick={addItem}><Plus className="h-4 w-4 mr-1.5" />{t.form.addItem}</Button>
        {!cleanItems.length && name.trim() && <p className="text-xs text-muted-foreground mt-2">{t.form.itemRequired}</p>}
      </div>
      {error && <p className="text-sm text-pnlNegative">{error}</p>}
    </LiteDrawer>
  );
}
